import { buildStudyJpegZip } from './buildStudyJpegZip';
import { triggerBrowserDownload } from './buildStudyDicomZipFilename';
import { buildStudyJpegFilename, getStudyFilenameParts } from './filenameUtils';
import { classifyDownloadError } from './downloadError';
import { DOWNLOAD_MESSAGES } from './downloadMessages';
import { renderJpegBlobFromImageId } from './renderJpegFromImageId';
import { collectStudyDicomInstances } from './resolveViewportStudy';
import type { DicomZipProgressCallback, StudyImageExportTarget } from './types';

export type ExportJpegTargetsResult =
  | { success: true; filename: string; imageCount: number }
  | { success: false; message: string };

export async function exportJpegTargets(
  studyInstanceUID: string,
  targets: StudyImageExportTarget[],
  extensionManager: AppTypes.ExtensionManager,
  onProgress?: DicomZipProgressCallback,
  servicesManager?: AppTypes.ServicesManager
): Promise<ExportJpegTargetsResult> {
  if (!targets.length) {
    return { success: false, message: DOWNLOAD_MESSAGES.NO_JPEG_SELECTED };
  }

  const instances = collectStudyDicomInstances(studyInstanceUID);

  try {
    if (targets.length === 1) {
      const target = targets[0];
      const jpegBlob = await renderJpegBlobFromImageId(target.imageId, extensionManager);
      const { studyDate, studyId } = getStudyFilenameParts(
        studyInstanceUID,
        instances,
        servicesManager
      );
      const filename = buildStudyJpegFilename(studyDate, studyId, target.exportIndex);
      triggerBrowserDownload(jpegBlob, filename);

      return { success: true, filename, imageCount: 1 };
    }

    const { blob, filename, includedCount } = await buildStudyJpegZip(
      studyInstanceUID,
      targets,
      extensionManager,
      onProgress,
      servicesManager
    );

    if (!includedCount) {
      return { success: false, message: DOWNLOAD_MESSAGES.JPEG_CONVERSION_FAILED };
    }

    triggerBrowserDownload(blob, filename);

    return { success: true, filename, imageCount: includedCount };
  } catch (error) {
    console.error('[ViewportDownload] JPEG export failed:', error);
    return { success: false, message: classifyDownloadError(error) };
  }
}
